import { StockCategoryFilter } from './stockCategories';

export type SentimentVoteOption = 'buy' | 'hold' | 'sell';

export interface SentimentVoteSummary {
  count: number;
  percentage: number;
  seeded?: boolean;
}

export interface SentimentSnapshot {
  buy: SentimentVoteSummary;
  hold: SentimentVoteSummary;
  sell: SentimentVoteSummary;
  totalVotes: number;
  userVoted: boolean;
  userVote: SentimentVoteOption | null;
  refreshedAt?: string;
}

export interface SentimentVoteRequest {
  vote: SentimentVoteOption;
  category?: StockCategoryFilter;
  date?: string;
  investmentCoins?: number;
}

export interface SentimentApiPayload {
  buy: SentimentVoteSummary;
  hold: SentimentVoteSummary;
  sell: SentimentVoteSummary;
  total_votes: number;
  userVoted: boolean;
  userVote: SentimentVoteOption | null;
}

export interface SentimentApiResponse {
  sentiment: SentimentApiPayload;
  position: any | null; // For future coin system integration
}

export interface SentimentApiEnvelope {
  success: boolean;
  data: SentimentApiResponse;
  message?: string;
  timestamp?: string;
}

export interface SentimentHistoryPoint {
  datePlayed: string;
  buy: SentimentVoteSummary;
  hold: SentimentVoteSummary;
  sell: SentimentVoteSummary;
  totalVotes: number;
}

export interface UseSentimentVotingOptions {
  symbol?: string | null;
  category?: StockCategoryFilter;
  date?: string | null;
  enabled?: boolean;
  isAuthenticated?: boolean;
}

export interface UseSentimentVotingReturn {
  sentiment: SentimentSnapshot;
  isLoading: boolean;
  isVoting: boolean;
  error: string | null;
  hasVoted: boolean;
  userVote: SentimentVoteOption | null;
  submitVote: (vote: SentimentVoteOption, investmentCoins?: number) => Promise<void>;
  refetch: () => Promise<void>;
}

export const SENTIMENT_SEED_COUNT_PER_OPTION = 0;
export const SENTIMENT_OPTIONS: SentimentVoteOption[] = ['buy', 'hold', 'sell'];

export const createSeededSentimentSnapshot = (): SentimentSnapshot => {
  const baseSummary: SentimentVoteSummary = {
    count: 0,
    percentage: 0,
    seeded: false
  };

  return {
    buy: { ...baseSummary },
    hold: { ...baseSummary },
    sell: { ...baseSummary },
    totalVotes: 0,
    userVoted: false,
    userVote: null,
    refreshedAt: new Date().toISOString()
  };
};

/**
 * User sentiment voting history item
 */
export interface UserSentimentHistoryItem {
  stockSymbol: string;
  companyName: string;
  category: StockCategoryFilter;
  userVote: SentimentVoteOption;
  votedAt: string;
  datePlayed: string;
}

/**
 * User sentiment history response from API
 */
export interface UserSentimentHistoryResponse {
  userId: string;
  history: UserSentimentHistoryItem[];
  total: number;
}

/**
 * Hook return type for useSentimentHistory
 */
export interface UseSentimentHistoryReturn {
  history: UserSentimentHistoryItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}
